package org.catharsis.spendtel.feature.presentation.financialgoal

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.spring
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavHostController
import org.catharsis.TVTally.theme.deepGreen
import org.catharsis.spendtel.core.navigation.AppRoutes
import org.catharsis.spendtel.feature.data.FinancialGoal
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelGradients
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelReportColors
import org.catharsis.spendtel.feature.presentation.expensereport.accentForIndex
import org.catharsis.spendtel.feature.presentation.home.HomeErrorText
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

private val mobileLayoutBreakpoint = 600.dp

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun FinancialGoalSelectionScreen(
    navHostController: NavHostController,
    isEditMode: Boolean = false,
    viewModel: FinancialGoalViewModel = koinViewModel { parametersOf(isEditMode) },
) {
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.effect.collect { effect ->
            when (effect) {
                FinancialGoalEffect.NavigateHome -> {
                    navHostController.navigate(AppRoutes.Home.name) {
                        popUpTo(AppRoutes.FinancialGoal.name) { inclusive = true }
                    }
                }
                FinancialGoalEffect.NavigateBack -> {
                    navHostController.popBackStack()
                }
            }
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(SpendTelReportColors.canvas),
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
            val useMobileBubbleCloud = maxWidth < mobileLayoutBreakpoint

            if (useMobileBubbleCloud) {
                MobileFinancialGoalContent(
                    state = state,
                    onGoalSelected = { viewModel.postEvent(FinancialGoalEvent.OnSelectGoal(it)) },
                    onContinue = { viewModel.postEvent(FinancialGoalEvent.OnContinue) },
                )
            } else {
                WebFinancialGoalContent(
                    state = state,
                    onGoalSelected = { viewModel.postEvent(FinancialGoalEvent.OnSelectGoal(it)) },
                    onContinue = { viewModel.postEvent(FinancialGoalEvent.OnContinue) },
                )
            }
        }
    }
}

@Composable
private fun MobileFinancialGoalContent(
    state: FinancialGoalState,
    onGoalSelected: (FinancialGoal) -> Unit,
    onContinue: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .statusBarsPadding()
            .imePadding()
            .navigationBarsPadding()
            .padding(horizontal = 20.dp),
    ) {
        Column(
            modifier = Modifier
                .weight(1f)
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Spacer(modifier = Modifier.height(16.dp))

            FinancialGoalHero(compact = true)

            Spacer(modifier = Modifier.height(24.dp))

            FinancialGoalHeader(isEditMode = state.isEditMode)

            Spacer(modifier = Modifier.height(20.dp))

            FinancialGoalBubbleCloud(
                selectedGoal = state.selectedGoal,
                onGoalSelected = onGoalSelected,
            )

            state.errorMessage?.let { message ->
                Spacer(modifier = Modifier.height(16.dp))
                HomeErrorText(message = message)
            }

            Spacer(modifier = Modifier.height(16.dp))
        }

        FinancialGoalContinueButton(
            selectedGoal = state.selectedGoal,
            isSaving = state.isSaving,
            isEditMode = state.isEditMode,
            onContinue = onContinue,
            modifier = Modifier.padding(bottom = 12.dp),
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun WebFinancialGoalContent(
    state: FinancialGoalState,
    onGoalSelected: (FinancialGoal) -> Unit,
    onContinue: () -> Unit,
) {
    val scrollState = rememberScrollState()

    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
            .padding(horizontal = 24.dp, vertical = 32.dp),
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(
            modifier = Modifier
                .widthIn(max = 720.dp)
                .fillMaxWidth()
                .verticalScroll(scrollState),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            FinancialGoalHero()

            Spacer(modifier = Modifier.height(28.dp))

            FinancialGoalHeader(isEditMode = state.isEditMode)

            Spacer(modifier = Modifier.height(28.dp))

            FlowRow(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(12.dp, Alignment.CenterHorizontally),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                FinancialGoal.selectableGoals.forEachIndexed { index, goal ->
                    FinancialGoalPillBubble(
                        goal = goal,
                        selected = state.selectedGoal == goal,
                        accentColor = accentForIndex(index),
                        onClick = { onGoalSelected(goal) },
                    )
                }
            }

            state.errorMessage?.let { message ->
                Spacer(modifier = Modifier.height(16.dp))
                HomeErrorText(message = message)
            }

            Spacer(modifier = Modifier.height(32.dp))

            FinancialGoalContinueButton(
                selectedGoal = state.selectedGoal,
                isSaving = state.isSaving,
                isEditMode = state.isEditMode,
                onContinue = onContinue,
            )
        }
    }
}

@Composable
private fun FinancialGoalHeader(
    modifier: Modifier = Modifier,
    isEditMode: Boolean = false,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(
            text = if (isEditMode) "Update your financial goal" else "What's your top financial goal?",
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Bold,
            color = SpendTelReportColors.ink,
            textAlign = TextAlign.Center,
        )

        Spacer(modifier = Modifier.height(10.dp))

        Text(
            text = if (isEditMode) {
                "Pick the goal that fits you best right now. Future reports will personalize insights around it."
            } else {
                "Pick the one that matters most right now. We'll use it to personalize your spending stories and insights."
            },
            style = MaterialTheme.typography.bodyLarge,
            color = SpendTelReportColors.inkMuted,
            textAlign = TextAlign.Center,
            lineHeight = 24.sp,
        )
    }
}

@Composable
private fun FinancialGoalContinueButton(
    selectedGoal: FinancialGoal?,
    isSaving: Boolean,
    isEditMode: Boolean,
    onContinue: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Button(
        onClick = onContinue,
        enabled = selectedGoal != null && !isSaving,
        modifier = modifier
            .fillMaxWidth()
            .height(56.dp),
        shape = RoundedCornerShape(18.dp),
        colors = ButtonDefaults.buttonColors(
            containerColor = deepGreen,
            contentColor = Color.White,
        ),
    ) {
        if (isSaving) {
            CircularProgressIndicator(
                modifier = Modifier.size(22.dp),
                color = Color.White,
                strokeWidth = 2.dp,
            )
            Text(
                text = "Saving your goal…",
                modifier = Modifier.padding(start = 12.dp),
                fontWeight = FontWeight.Bold,
            )
        } else {
            Text(
                text = if (isEditMode) "Save goal" else "Continue to SpendTel",
                fontWeight = FontWeight.Bold,
                fontSize = 16.sp,
            )
        }
    }
}

@Composable
private fun FinancialGoalHero(
    modifier: Modifier = Modifier,
    compact: Boolean = false,
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(if (compact) 110.dp else 140.dp)
            .clip(RoundedCornerShape(if (compact) 24.dp else 28.dp))
            .background(SpendTelGradients.personality),
        contentAlignment = Alignment.Center,
    ) {
        Box(
            modifier = Modifier
                .size(if (compact) 72.dp else 84.dp)
                .clip(CircleShape)
                .background(Color.White.copy(alpha = 0.16f)),
            contentAlignment = Alignment.Center,
        ) {
            Icon(
                imageVector = financialGoalHeroIcon,
                contentDescription = null,
                tint = Color.White,
                modifier = Modifier.size(if (compact) 34.dp else 40.dp),
            )
        }
    }
}

@Composable
private fun FinancialGoalPillBubble(
    goal: FinancialGoal,
    selected: Boolean,
    accentColor: Color,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val backgroundColor by animateColorAsState(
        targetValue = if (selected) accentColor.copy(alpha = 0.16f) else Color.White,
        animationSpec = spring(stiffness = Spring.StiffnessMediumLow),
        label = "goalBubbleBackground",
    )
    val borderColor by animateColorAsState(
        targetValue = if (selected) accentColor else SpendTelReportColors.canvasDark,
        animationSpec = spring(stiffness = Spring.StiffnessMediumLow),
        label = "goalBubbleBorder",
    )

    Surface(
        modifier = modifier
            .shadow(if (selected) 8.dp else 2.dp, RoundedCornerShape(999.dp))
            .clip(RoundedCornerShape(999.dp))
            .clickable(onClick = onClick),
        shape = RoundedCornerShape(999.dp),
        color = backgroundColor,
    ) {
        Row(
            modifier = Modifier
                .border(1.5.dp, borderColor, RoundedCornerShape(999.dp))
                .padding(horizontal = 18.dp, vertical = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Box(
                modifier = Modifier
                    .size(36.dp)
                    .clip(CircleShape)
                    .background(
                        if (selected) accentColor.copy(alpha = 0.22f)
                        else accentColor.copy(alpha = 0.12f),
                    ),
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    imageVector = goal.icon(),
                    contentDescription = null,
                    tint = if (selected) accentColor else accentColor.copy(alpha = 0.85f),
                    modifier = Modifier.size(20.dp),
                )
            }
            Text(
                text = goal.title,
                style = MaterialTheme.typography.bodyLarge,
                fontWeight = if (selected) FontWeight.Bold else FontWeight.Medium,
                color = if (selected) SpendTelReportColors.ink else SpendTelReportColors.inkMuted,
            )
        }
    }
}
